/** @jsxImportSource @emotion/react */
import React from 'react';
import { Tooltip, Typography } from 'antd';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { useGetEnergyBalanceQuery } from '../../api/energyApi';
import { selectIsAuthenticated } from '../../store/slices/authSlice';
import brandMark from '../../assets/home/mark.webp';
import brandWordmark from '../../assets/home/wordmark.webp';
import chargeIcon from '../../assets/home/charge.webp';

const { Text } = Typography;

const styles = {
  wrap: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    padding: '4px 0 8px',
  }),
  brand: css({ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }),
  mark: css({ width: 54, height: 'auto', flexShrink: 0, display: 'block' }),
  wordmark: css({ width: 168, height: 'auto', display: 'block' }),
  charge: css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 16px 6px 10px',
    borderRadius: 999,
    background: '#FFFBEA',
    border: '1px solid #F0DFA6',
    boxShadow: '0 2px 6px rgba(120, 96, 20, 0.08)',
  }),
  chargeIcon: css({ width: 30, height: 30, display: 'block', flexShrink: 0 }),
  chargeText: css({ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }),
  chargeValue: css({ fontWeight: 700, color: '#6B5411', fontSize: 15 }),
  chargeLabel: css({ fontSize: 11, color: '#9A8B63' }),
  gauge: css({
    width: 72,
    height: 5,
    borderRadius: 999,
    background: '#EFE3BD',
    overflow: 'hidden',
    marginTop: 3,
  }),
  gaugeFill: (pct: number) =>
    css({
      width: `${pct}%`,
      height: '100%',
      borderRadius: 999,
      background: 'linear-gradient(90deg, #A5C563 0%, #E8B93F 100%)',
      transition: 'width 240ms ease',
    }),
};

const formatCharge = (value: number) => value.toLocaleString('ru-RU');

/**
 * Шапка главного экрана: бренд слева, заряд справа.
 *
 * Ни приветствия, ни погоды здесь нет намеренно. Погода привязана к сейву, а
 * главный экран — над сейвами, так что плашка показывала бы «нет данных» ровно
 * до тех пор, пока игрок не провалится внутрь конкретного участка.
 */
const HomeHeader: React.FC = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { data: energy } = useGetEnergyBalanceQuery(undefined, { skip: !isAuthenticated, pollingInterval: 60_000 });

  const balance = energy?.balance;
  const stockCap = energy?.stockCap || 0;
  const pct = stockCap > 0 && typeof balance === 'number'
    ? Math.max(0, Math.min(100, (balance / stockCap) * 100))
    : 0;

  const tooltip = typeof balance === 'number'
    ? `Заряд машины времени: ${formatCharge(balance)} из ${formatCharge(stockCap)} посадко-дней. `
      + `Восстанавливается ${((energy?.dailyGrant || 0) / 1440).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} посадко-дня в минуту ожидания. `
      + 'День симуляции стоит столько посадко-дней, сколько у вас активных посадок.'
    : 'Заряд машины времени — топливо симуляции';

  return (
    <div css={styles.wrap}>
      <div css={styles.brand}>
        <img src={brandMark} alt="" css={styles.mark} />
        <img src={brandWordmark} alt="Verdomatic by Canifole" css={styles.wordmark} />
      </div>

      <Tooltip title={tooltip}>
        <span css={styles.charge}>
          <img src={chargeIcon} alt="" css={styles.chargeIcon} />
          <span css={styles.chargeText}>
            <span css={styles.chargeValue}>
              {typeof balance === 'number' ? formatCharge(balance) : '—'}
            </span>
            <Text css={styles.chargeLabel}>заряд</Text>
            <span css={styles.gauge}>
              <span css={styles.gaugeFill(pct)} />
            </span>
          </span>
        </span>
      </Tooltip>
    </div>
  );
};

export default HomeHeader;
